import type { EnhancedVerificationRequest } from '@common/types/treefort';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import SVGSpinner from '@/components/Buttons/SVGSpinner';
import DataTable, { type Column } from '@/components/DataTable';
import EditableViewComponent from '@/components/ReadOnlyComponent/EditableViewComponent';
import { useContactsByProspectVersionId } from '@/queries/contacts';
import { invalidateAfterProspectChange, useSAIDV, useSAIDVDetails } from '@/queries/saidv';
import { getDownloadLink, getFileDetails } from '@/services/FileService';
import { updateCustomFileNumber } from '@/services/IDVService';
import { theme } from '@/theme';
import { convertUTCDateToEST } from '@/utils/datetime';
import { successSnackbar } from '@/utils/snackbar';

interface Props {
    prospectId?: string;
    prospectVerId?: number;
    onWithdraw: (row: EnhancedVerificationRequest) => void;
    onResend: (row: EnhancedVerificationRequest) => void;
    onReinitiate: (row: EnhancedVerificationRequest) => void;
    onUpdate: (row: EnhancedVerificationRequest) => void;
}

export default function StandAloneIDVTable({
    prospectId,
    prospectVerId,
    onWithdraw,
    onResend,
    onReinitiate,
    onUpdate,
}: Props) {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

    const { data: prospect } = useSAIDV(prospectId);
    const { data: verificationDetails = [], isLoading } = useSAIDVDetails(prospectId);
    const { data: contacts = [] } = useContactsByProspectVersionId(prospectVerId);

    const fetchReviewFile = async (fileId: string, fileName?: string) => {
        try {
            const response = await fetch(fileId);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? 'idv-review-report.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleReviewReportDownload = async (reviewFileId: string) => {
        if (loadingStates[reviewFileId]) return;

        try {
            setLoadingStates((prev) => ({
                ...prev,
                [reviewFileId]: true,
            }));
            const downloadLink = getDownloadLink(reviewFileId);
            const fileDetails = await getFileDetails(reviewFileId);
            fetchReviewFile(downloadLink, fileDetails?.data?.fileName);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingStates((prev) => ({
                ...prev,
                [reviewFileId]: false,
            }));
        }
    };

    const statusColors: Record<string, { color: string }> = {
        'Request Pending': { color: '#FFB300' },
        Cancelled: { color: '#F44336' },
        'Review Report': { color: '#4CAF50' },
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formattedRequests: EnhancedVerificationRequest[] = verificationDetails.map((row) => {
        const matchedContact = contacts.find(
            (contact) => contact.contactId === row.requesterClientRequestId,
        );

        return {
            ...row,
            userName: matchedContact ? matchedContact.signingName : '',
        };
    });

    const columns: Column<EnhancedVerificationRequest>[] = [
        {
            id: 'fileNumber',
            label: 'File Number',
        },
        {
            id: 'userName',
            label: 'User Name',
        },
        {
            id: 'email',
            label: 'User Email',
        },
        {
            id: 'requestStatus',
            label: 'IDV Status',
            _render: (rowData) => {
                const status = rowData.requestStatus ?? '';
                const reviewFileId = rowData.reviewFileId;
                const isReviewReport = status === 'Review Report' && !!reviewFileId;
                const isLoading = reviewFileId ? loadingStates[reviewFileId] : false;

                return (
                    <Stack
                        direction="row"
                        onClick={
                            isReviewReport && reviewFileId
                                ? () => handleReviewReportDownload(reviewFileId)
                                : undefined
                        }
                        sx={{
                            fontWeight: 'bold',
                            lineHeight: 1,
                            borderRadius: 1,
                            color: isReviewReport ? '#1E90FF' : statusColors[status]?.color,
                            cursor: isReviewReport ? 'pointer' : 'default',
                            textDecoration: isReviewReport ? 'underline' : 'none',
                            '&:hover': {
                                textDecoration: isReviewReport ? 'underline' : 'none',
                            },
                            opacity: isLoading ? 0.75 : 1,
                        }}
                    >
                        {isLoading && <SVGSpinner stroke="#1E90FF" height={16} width={16} />}
                        {status}
                    </Stack>
                );
            },
        },
        {
            id: 'createdAt',
            label: 'IDV Sent Date & Time',
            _render: (rowData) =>
                rowData.createdAt ? convertUTCDateToEST(rowData.createdAt, 'M/d/yyyy h:mm a') : '',
        },
        {
            id: 'verificationLink',
            label: 'IDV Link',
            _render: (rowData) => {
                const link = rowData.verificationLink?.trim();

                const copyToClipboard = () => {
                    if (link) {
                        navigator.clipboard.writeText(link);
                    }
                };

                return (
                    <Box className="flex items-center gap-2">
                        {link && (
                            <IconButton size="small" onClick={copyToClipboard}>
                                <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'withdraw',
            label: '',
            _render: (rowData) => {
                const status = rowData.requestStatus?.trim() ?? '';
                const isResend = status === 'Cancelled' || status === 'Review Report';

                const estDateStr = rowData.createdAt
                    ? convertUTCDateToEST(rowData.createdAt, 'yyyy-MM-dd HH:mm:ss')
                    : '';
                const createdAtEST = new Date(estDateStr);
                const now = new Date();

                const diffInMs = now.getTime() - createdAtEST.getTime();
                const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

                const isOldReviewReport = status === 'Review Report' && diffInDays > 14;
                const handleClick = () => {
                    if (isOldReviewReport) {
                        onReinitiate(rowData);
                    } else if (isResend) {
                        onResend(rowData);
                    } else {
                        onWithdraw(rowData);
                    }
                };
                return (
                    <Button variant="contained" color="primary" size="small" onClick={handleClick}>
                        {isResend ? 'Resend' : 'Withdraw'}
                    </Button>
                );
            },
        },
        {
            id: 'update',
            label: '',
            _render: (rowData) => {
                const status = rowData.requestStatus?.trim() ?? '';

                return (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={status !== 'Request Pending'}
                        onClick={() => onUpdate(rowData)}
                    >
                        Update
                    </Button>
                );
            },
        },
    ];

    const handleChange = async (value: string) => {
        const customFileNumber = prospect?.customFileNumber ?? '';
        if ((!value && customFileNumber === '') || value === customFileNumber) return;

        if (!prospectId) return;

        try {
            await updateCustomFileNumber({
                customFileNumber: value,
                prospectId,
            });
            await invalidateAfterProspectChange(queryClient, prospectId);
            successSnackbar('Custom file number updated successfully!');
        } catch (error) {
            console.error('API error:', error);
        }
    };

    return (
        <Box
            sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            <Box
                sx={{
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.neutral.borderColor}`,
                    backgroundColor: 'neutral.white',
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '18px',
                    height: 'fit-content',
                    width: '40%',
                }}
            >
                <EditableViewComponent
                    label="Custom file number"
                    value={prospect?.customFileNumber ?? ''}
                    isEditable={true}
                    onChange={handleChange}
                    maxLength={400}
                    errorMessage="Custom file number must be less than 400 characters."
                />
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <Typography sx={{ marginBottom: '16px' }} variant="h3">
                        ID Verification Details
                    </Typography>
                    <DataTable
                        generateKeyFrom="clientRequestId"
                        isLoading={isLoading}
                        columns={columns}
                        rows={formattedRequests}
                        customStyles={{
                            border: `1px solid ${theme.palette.neutral.borderColor}`,
                            boxShadow: 'none',
                        }}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
